import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { EASE } from "@/lib/motion";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "ai",
      text: "Hallo! Ik ben de digitale assistent van Boris en Timo. Vraag me alles over het uitruimen van je garage, zolder, schuur of berging.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(`${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`);
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo(0, listRef.current.scrollHeight);
  }, [messages, busy]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "ai", text: "" }]);
    try {
      const r = await fetch(`${BACKEND}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, sessionId: sessionRef.current }),
      });
      if (!r.ok || !r.body) throw new Error("geen verbinding");
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let payload;
          try {
            payload = JSON.parse(line.slice(5));
          } catch {
            continue;
          }
          if (payload.delta) {
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "ai", text: (c[c.length - 1]?.text || "") + payload.delta };
              return c;
            });
          } else if (payload.error) {
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "ai", text: payload.error };
              return c;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = {
          role: "ai",
          text: "De assistent kon geen antwoord ophalen. Bel of app gerust 06 1376 7661.",
        };
        return c;
      });
    }
    setBusy(false);
  };

  return (
    <div className="chat-root">
      <AnimatePresence>
        {open ? (
          <motion.section
            className="chat-panel"
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.3, ease: EASE }}
          >
            <header className="chat-head">
              <div>
                <strong>Blankwater assistent</strong>
                <span>Antwoordt meteen, dag en nacht</span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Chat sluiten"
                data-testid="chat-close"
              >
                ×
              </button>
            </header>
            <div className="chat-list" ref={listRef} data-testid="chat-list">
              {messages.map((m, i) => (
                <div key={i} className={`chat-msg chat-msg-${m.role}`} data-testid={`chat-message-${m.role}`}>
                  {m.text || (busy && i === messages.length - 1 ? "…" : "")}
                </div>
              ))}
            </div>
            <form onSubmit={send} className="chat-form">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Stel je vraag…"
                maxLength={1000}
                data-testid="chat-input"
              />
              <button type="submit" disabled={busy} data-testid="chat-send" aria-label="Versturen">
                ↗
              </button>
            </form>
          </motion.section>
        ) : null}
      </AnimatePresence>
      <motion.button
        type="button"
        className="chat-toggle"
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-toggle"
        aria-label={open ? "Chat sluiten" : "Chat openen"}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1.1, type: "spring", stiffness: 260, damping: 18 }}
      >
        {open ? (
          "×"
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M4 6.5A3.5 3.5 0 0 1 7.5 3h9A3.5 3.5 0 0 1 20 6.5v6a3.5 3.5 0 0 1-3.5 3.5H9l-4.2 3.6c-.5.4-1.3 0-1.3-.7L4 6.5Z"
              fill="currentColor"
            />
          </svg>
        )}
      </motion.button>
    </div>
  );
}
